import { AnimatePresence, motion } from "framer-motion"
import { useData } from "../../contexts/DataContextProvider"
import { useProfile } from "../../contexts/ProfileContextProvider"
import CompanyCard from "./CompanyCard"
import { RefreshCw } from "react-feather"
import Button from "../UIComponents/Button"
import useCompanySwipeStack from "../../hooks/useCompanySwipeStack"
import { useCallback, useEffect, useState } from "react"

function MatchCompany({ onBackgroundChange, onInitialChange, themeColor }) {
    const { data } = useData()
    const { favorites, contextSetFavorites } = useProfile()
    const [isTransitioning, setIsTransitioning] = useState(false)

    const {
        currentCompany,
        handleSwipe,
        refreshStack,
        clearRejectedCompanies,
        isStackEmpty,
        canRefresh,
        allCompaniesLiked,
    } = useCompanySwipeStack(data, favorites)

    // Nollställning när det är slut på företag
    useEffect(() => {
        if (isStackEmpty) {
            onBackgroundChange(themeColor)
            onInitialChange(true)
        }
    }, [isStackEmpty, themeColor, onBackgroundChange, onInitialChange])

    const handleRefresh = () => {
        setIsTransitioning(false)
        onInitialChange(true)
        clearRejectedCompanies()
        refreshStack()
        onBackgroundChange(themeColor)
    }

    const handleOnDragChange = (backgroundColor) => {
        onInitialChange(false)
        onBackgroundChange(backgroundColor)
    }

    const handleLike = useCallback(() => {
        if (currentCompany && !isTransitioning) {
            setIsTransitioning(true)
            requestAnimationFrame(() => {
                contextSetFavorites(currentCompany)
                handleSwipe(true)
            })
        }
    }, [currentCompany, isTransitioning, contextSetFavorites, handleSwipe])

    const handleRejection = useCallback(() => {
        if (!isTransitioning) {
            setIsTransitioning(true)
            handleSwipe(false)
        }
    }, [isTransitioning, handleSwipe])

    // Returnera detta om alla företag är gillade (i favoriter)
    if (allCompaniesLiked) {
        return (
            <div className="w-full h-full grid place-items-center relative">
                <div className="flex flex-col m-4">
                    <p className="pb-3 text-center">
                        Du har gillat alla företag!
                        <br />
                        Gå till dina favoriter för att kolla in dem.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <motion.div
            className="w-full h-full flex justify-center items-center sm:mb-0 mb-20 relative "
            data-disable-swipe
        >
            {isStackEmpty && canRefresh ? (
                <div className="flex flex-col m-4">
                    <p className="pb-3 text-center fg-color-1">
                        Inga fler företag kunde hittas. Klicka nedan för att
                        ladda om.
                    </p>
                    <Button onClick={handleRefresh} classes={"p-4 mx-auto"}>
                        <RefreshCw size={32} />
                    </Button>
                </div>
            ) : (
                <AnimatePresence
                    mode="wait"
                    onExitComplete={() => {
                        setIsTransitioning(false)
                        onInitialChange(true)
                    }}
                    reducedMotion="user"
                >
                    {currentCompany && !isTransitioning && (
                        <CompanyCard
                            key={`company-${currentCompany.monternummer}`}
                            data={currentCompany}
                            setInitial={onInitialChange}
                            themeColor={themeColor}
                            onDragChange={handleOnDragChange}
                            handleLike={handleLike}
                            handleRejection={handleRejection}
                        />
                    )}
                </AnimatePresence>
            )}
        </motion.div>
    )
}

export default MatchCompany
